import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { InputSwitch } from 'primeng/inputswitch';
import { TableLazyLoadEvent } from 'primeng/table';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { ContadorService } from '../../services/contador/contador.service';
import { DespesaAcessoriaService } from '../../services/contador/despesa-acessoria.service';
import {
  MesReferencia,
  MeuDescontoVigenteDTO,
  SolicitacaoContratoRecebidaDTO,
} from '../../models/contador/contador.model';
import { ContratoInfoVM } from '../../models/contador/contrato-info.model';
import { Moeda, MOEDA_CONFIG, MoedaConfig } from '../../enums/moeda.enum';
import {
  DespesaAcessoriaHistoricoDTO,
  DespesaAcessoriaPendenteDTO,
} from '../../models/contador/despesa-acessoria.model';
import {
  EstadoClienteParcela,
  ParcelaContratoClienteFiltroDTO,
  ParcelaContratoConsultaDTO,
} from '../../models/contador/parcela-contrato.model';
import { PeriodoFiltro } from '../../models/dashboard/periodo-filtro.model';
import { TipoDespesaBackendDTO } from '../../models/gerenciar-contas/despesa.model';
import {
  ehIvaTipoDespesa,
  formatarValorMoedaContrato,
} from '../../shared/utils/despesa-contador.util';

/**
 * Tela de detalhes das notificacoes do contador (lado CLIENTE).
 *
 * Reune, em uma unica pagina, as duas pendencias que o cliente precisa
 * decidir:
 *
 * 1. Solicitacoes de contrato do contador (PENDENTE) — o cliente aceita
 *    (PATCH /contador/contratos/{id}/aceitar) ou recusa
 *    (PATCH /contador/contratos/{id}/recusar).
 * 2. Despesas acessorias pendentes de aprovacao — o cliente aprova
 *    (PATCH /cliente/despesas-contador/{id}/aprovar, gera os lancamentos) ou
 *    recusa (PATCH .../recusar). Despesas com tipo de gasto IVA recebem
 *    destaque visual.
 *
 * Toda a regra/posse esta no backend; a tela apenas consome os endpoints via
 * `ContadorService` e `DespesaAcessoriaService` (ja existentes).
 */
@Component({
  selector: 'app-notificacoes-contador',
  templateUrl: './notificacoes-contador.component.html',
  styleUrls: ['./notificacoes-contador.component.scss'],
  providers: [MessageService],
})
export class NotificacoesContadorComponent implements OnInit, OnDestroy {
  // Contrato ATIVO do cliente com o contador (secao "Meu contrato"). `null` =
  // sem contrato ativo (o cliente ainda pode ter uma solicitacao pendente abaixo).
  contratoVigente: MeuDescontoVigenteDTO | null = null;
  carregandoContratoVigente = false;
  erroContratoVigente = false;

  // Autonomia do contador (bloco no card "Meu contrato"). Espelho do switch,
  // sincronizado com `contratoVigente.autonomiaContador`. NUNCA otimista: so muda
  // pelo RETORNO do PATCH; em erro, reverte para o estado real do contrato.
  autonomiaAtiva = false;
  /** Bloqueia o switch enquanto o PATCH de autonomia esta em curso. */
  definindoAutonomia = false;

  /**
   * Referencia ao p-inputSwitch de autonomia. Necessaria para REVERTER o switch
   * ao cancelar/fechar o dialogo de confirmacao: o PrimeNG atualiza o estado
   * interno do switch no clique (otimista) e, ao reatribuir o MESMO valor
   * anterior ao [(ngModel)] no mesmo ciclo, o Angular nao detecta mudanca e nao
   * chama `writeValue` — deixando o switch visualmente ligado. Chamamos
   * `writeValue` diretamente nessa referencia para forcar a sincronizacao.
   */
  @ViewChild('autonomiaSwitch') private autonomiaSwitch?: InputSwitch;

  // ===========================================================================
  // Detalhe do contrato: card compartilhado app-contrato-info-card (somente
  // leitura), alimentado pelo mapeamento MeuDescontoVigenteDTO -> ContratoInfoVM.
  // ===========================================================================
  contratoInfoVM: ContratoInfoVM | null = null;
  /** Config de formatacao da moeda do contrato (simbolo/casas/locale). */
  moedaConfig: MoedaConfig = MOEDA_CONFIG[Moeda.GUARANI];
  /** Mes de referencia do contrato (exibido como TEXTO — nao editavel pelo cliente). */
  contratoMesReferencia: MesReferencia | null = null;
  /** Opcoes { label, value } para resolver o rotulo do mes de referencia no card. */
  mesReferenciaOptions: { label: string; value: MesReferencia }[] = [];

  // Solicitacoes de contrato
  contratos: SolicitacaoContratoRecebidaDTO[] = [];
  carregandoContratos = false;
  erroContratos = false;

  // Despesas acessorias pendentes
  despesas: DespesaAcessoriaPendenteDTO[] = [];
  carregandoDespesas = false;
  erroDespesas = false;

  /**
   * Visibilidade do BLOCO DE ABAS inteiro ("Despesas do contador" / "Historico
   * de gastos" / "Parcelas do contrato"). Comeca `true` (FALHA SEGURA: na
   * duvida, mostrar) e so vira `false` quando comprovamos que nao ha NADA para
   * ver — sem contrato ativo, zero despesas pendentes e zero historico.
   *
   * Atencao: esconder apenas por "nao tem contrato ATIVO" seria ERRADO. As
   * despesas e o historico pertencem a EMPRESA, nao ao contrato vigente: o
   * backend filtra por empresa cliente (todos os contratos dela, inclusive os
   * CANCELADOS). Um cliente cujo contrato acabou continua tendo historico — e
   * pode ate ter uma despesa PENDENTE para decidir, ja que o cancelamento do
   * contrato nao cancela as despesas.
   */
  mostrarAbas = true;

  /**
   * Latch da avaliacao acima: uma vez decidida, a visibilidade NUNCA mais e
   * recalculada na sessao. Impede que o bloco desapareca debaixo do usuario
   * (ex.: ele aprova a ULTIMA despesa pendente e a lista de pendentes zera).
   */
  private abasAvaliadas = false;

  /**
   * Marcas de CONCLUSAO da primeira carga de cada fonte que alimenta a decisao
   * acima (sucesso ou erro). A avaliacao so acontece com as duas resolvidas.
   *
   * Sao flags proprias, e nao os `carregando*`: no ngOnInit as tres cargas sao
   * disparadas em sequencia, entao uma resposta SINCRONA do contrato chegaria
   * com `carregandoDespesas` ainda `false` (a carga nem comecou) e a decisao
   * sairia sem as despesas. Com as marcas, a ordem de chegada nao importa.
   */
  private contratoVigenteResolvido = false;
  private despesasResolvidas = false;

  // Historico de despesas ja decididas (aprovadas/recusadas) — paginacao/ordenacao
  // SERVER-SIDE (p-table lazy + paginador externo). `historico` = pagina atual.
  historico: DespesaAcessoriaHistoricoDTO[] = [];
  carregandoHistorico = false;
  erroHistorico = false;

  // Paginacao server-side do historico (layout separado, padrao da tela de Grupo/Area do Contador).
  historicoFirst = 0;
  historicoRows = 10;
  historicoTotalRecords = 0;
  historicoPageSizeOptions = [
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];

  // Estilos estaticos das tabelas (campos readonly em vez de objeto literal no
  // template: literal inline criaria um objeto novo a cada change detection).
  // min-width garante rolagem horizontal (em vez de espremer colunas) no mobile.
  readonly tabelaPendentesStyle = { 'min-width': '60rem', width: '100%', 'table-layout': 'fixed' };
  readonly tabelaHistoricoStyle = { 'min-width': '60rem', width: '100%' };

  /**
   * True apos o primeiro onLazyLoad do historico (primeira ativacao da aba 2 —
   * o conteudo da aba usa pTemplate="content", renderizado sob demanda). Evita
   * recarregar uma tabela que nunca foi exibida apos aprovar/recusar.
   */
  private historicoIniciado = false;

  /**
   * Tamanho de pagina da ULTIMA carga solicitada do historico. O p-dropdown de
   * "itens por pagina" emite um ngModelChange na primeira renderizacao (o
   * container so aparece apos a primeira resposta) sem mudanca real de valor —
   * comparar com este campo evita um GET duplicado nessa emissao.
   */
  private historicoRowsSolicitado = 10;

  /**
   * Ordenacao ativa no formato Spring `campo,direcao` (ex.: `valor,asc`).
   * `undefined` => backend aplica o default (`dataDecisao,desc`). Preservada
   * entre trocas de pagina e recargas pos-acao.
   */
  private historicoSort: string | undefined;

  // ===========================================================================
  // Parcelas do contrato do CLIENTE (aba propria "Parcelas do contrato",
  // read-only). Reusa o grid compartilhado app-parcelas-contrato em modo cliente
  // (sem acoes e sem colunas da contraparte contadora; mantem a coluna Saldo,
  // que so o contador esconde). Paginacao/
  // ordenacao/filtros SERVER-SIDE, espelhando o pai do contador (Operar Cliente).
  // A carga e disparada na 1a abertura da aba (ver onTabViewChange).
  // ===========================================================================
  parcelasCliente: ParcelaContratoConsultaDTO[] = [];
  carregandoParcelasCliente = false;
  erroParcelasCliente = false;

  // Paginacao server-side (paginador separado dentro do grid compartilhado).
  parcelasClienteFirst = 0;
  parcelasClienteRows = 10;
  parcelasClienteTotalRecords = 0;

  // Ordenacao por cabecalho (formato Page/Spring). Default = numeroParcela ASC
  // para o indicador visual bater com a ordem carregada.
  parcelasClienteSortField: string | null = 'numeroParcela';
  parcelasClienteSortOrder = 1;

  // Filtros: periodo (por dataReferencia) + situacao (EstadoClienteParcela).
  parcelasClienteDe: string | undefined = undefined;
  parcelasClienteAte: string | undefined = undefined;
  parcelasClienteSituacao: EstadoClienteParcela | null = null;
  /** Opcoes { label, value } do dropdown de situacao; rotulos no idioma atual. */
  parcelasClienteSituacaoOptions: { label: string; value: EstadoClienteParcela | null }[] = [];

  /** True apos disparar a 1a carga (na 1a ativacao da aba). Guarda a carga lazy. */
  private parcelasClienteIniciado = false;

  /** Estados possiveis do lado a PAGAR (cliente) — ordem exibida no dropdown. */
  private static readonly SITUACOES_PARCELA_CLIENTE: EstadoClienteParcela[] = [
    'PENDENTE',
    'PAGA',
    'CANCELADA',
  ];

  /**
   * Indice (0-based) da aba "Parcelas do contrato" no p-tabView. As duas
   * primeiras abas ("Despesas do contador" e "Historico de gastos") sao
   * INCONDICIONAIS; a aba de parcelas so e renderizada quando ha contrato ATIVO
   * com id (*ngIf no p-tabPanel) e, por ser a ULTIMA, ocupa sempre o indice 2
   * quando existe. Usado por onTabViewChange para disparar a carga lazy.
   */
  private static readonly PARCELAS_TAB_INDEX = 2;

  /** Bloqueia botoes enquanto uma acao (aceitar/recusar/aprovar) esta em curso. */
  processando = false;

  /**
   * Exibe o botao "Voltar" apenas quando a navegacao veio do dashboard (card de
   * pendencias) ou do sino do header — ambos passam o query param `origem`.
   * Acesso pelo menu "Meu Contador" ou direto: sem `origem` => sem Voltar.
   */
  mostrarVoltar = false;

  // ===========================================================================
  // Dialogo de confirmacao (padrao "wave" do sistema — ver docs/ai/FORM_UI.md e a
  // tela de referencia /contador/aprovacao). Substitui o p-confirmDialog generico
  // (cru, fora do padrao). Um UNICO dialogo generico atende as quatro acoes da
  // tela (aceitar/recusar contrato, aprovar/recusar despesa); os campos abaixo
  // definem titulo, cor do header, mensagem, icone e o botao de confirmar.
  // ===========================================================================
  // Todos os campos sao definidos por abrirConfirmacao() antes de exibir; os
  // valores iniciais vazios nunca renderizam.
  confirmVisivel = false;
  confirmTitulo = '';
  confirmSubtitulo = '';
  confirmMensagem = '';
  confirmIcone = '';
  // Variante de cor do header wave: 'info' (azul, acoes positivas) ou 'danger'
  // (vermelho, recusas). Aplicada como classe `.dialog-wave-header--{variante}`
  // e tambem define a cor do botao de confirmar (danger nas recusas).
  confirmVariante: 'info' | 'danger' = 'info';
  confirmBotaoLabel = '';

  /** Acao executada somente se o usuario confirmar; definida ao abrir o dialogo. */
  private confirmAcao: (() => void) | null = null;

  /**
   * Acao executada quando o dialogo e FECHADO SEM confirmar (botao Cancelar, X,
   * clique fora ou onHide). Usada, por exemplo, para reverter o switch de
   * autonomia ao estado anterior. Opcional: nem toda confirmacao precisa desfazer
   * algo (aceitar/recusar contrato e aprovar/recusar despesa nao definem).
   */
  private confirmAoCancelar: (() => void) | null = null;

  private langChangeSub?: Subscription;

  constructor(
    private contadorService: ContadorService,
    private despesaService: DespesaAcessoriaService,
    private messageService: MessageService,
    private translate: TranslateService,
    private location: Location,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.mostrarVoltar = this.route.snapshot.queryParamMap.get('origem') != null;
    this.buildMesReferenciaOptions();
    this.buildParcelasClienteSituacaoOptions();
    this.carregarContratoVigente();
    this.carregarContratos();
    this.carregarDespesas();
    // O historico NAO e carregado aqui: o conteudo da aba 2 usa
    // pTemplate="content" (renderiza na primeira ativacao da aba) e o
    // (onLazyLoad) do p-table dispara a primeira carga nesse momento.
    // O `nome` do tipo de despesa vem localizado do backend (Accept-Language);
    // recarrega ao trocar de idioma para manter os rotulos consistentes.
    // Usa switchMap para cancelar uma recarga anterior ainda em curso, evitando
    // resposta fora de ordem em trocas rapidas de idioma.
    this.langChangeSub = this.translate.onLangChange
      .pipe(
        tap(() => {
          this.buildMesReferenciaOptions();
          this.buildParcelasClienteSituacaoOptions();
        }),
        switchMap(() => this.carregarDespesas$()),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
  }

  voltar(): void {
    this.location.back();
  }

  // ===========================================================================
  // Dialogo de confirmacao (padrao wave)
  // ===========================================================================

  /**
   * Abre o dialogo de confirmacao (padrao wave) configurado para uma acao. A
   * `acao` so e executada se o usuario confirmar (ver confirmarAcao()). Titulo,
   * mensagem e rotulo do botao sao chaves de i18n (traduzidas no template); o
   * subtitulo e um texto ja pronto (ex.: nome do contador). O icone tambem e
   * usado no botao de confirmar, e a cor do botao deriva da variante.
   */
  private abrirConfirmacao(config: {
    titulo: string;
    subtitulo?: string;
    mensagem: string;
    icone: string;
    variante: 'info' | 'danger';
    botaoLabel: string;
    acao: () => void;
    aoCancelar?: () => void;
  }): void {
    this.confirmTitulo = config.titulo;
    this.confirmSubtitulo = config.subtitulo ?? '';
    this.confirmMensagem = config.mensagem;
    this.confirmIcone = config.icone;
    this.confirmVariante = config.variante;
    this.confirmBotaoLabel = config.botaoLabel;
    this.confirmAcao = config.acao;
    this.confirmAoCancelar = config.aoCancelar ?? null;
    this.confirmVisivel = true;
  }

  /**
   * Fecha o dialogo SEM confirmar (botao Cancelar, X, clique fora ou onHide) e
   * executa o callback de cancelamento, se houver (ex.: reverter o switch de
   * autonomia). Idempotente: ao confirmar, `confirmarAcao()` ja limpou o callback
   * antes de o (onHide) disparar este metodo — evitando desfazer a acao.
   */
  fecharConfirmacao(): void {
    const aoCancelar = this.confirmAoCancelar;
    this.limparConfirmacao();
    aoCancelar?.();
  }

  /** Executa a acao confirmada e fecha o dialogo (sem disparar o cancelamento). */
  confirmarAcao(): void {
    const acao = this.confirmAcao;
    this.limparConfirmacao();
    acao?.();
  }

  /** Fecha o dialogo e limpa os callbacks (confirmar e cancelar). */
  private limparConfirmacao(): void {
    this.confirmVisivel = false;
    this.confirmAcao = null;
    this.confirmAoCancelar = null;
  }

  // ===========================================================================
  // Contrato ativo (secao "Meu contrato")
  // ===========================================================================

  /**
   * Carrega o contrato ATIVO do cliente com o contador. O backend devolve corpo
   * vazio (null) quando nao ha contrato ativo — nesse caso a secao mostra o
   * estado "sem contrato". Erro real exibe o estado de erro com "tentar novamente".
   */
  carregarContratoVigente(): void {
    this.carregandoContratoVigente = true;
    this.erroContratoVigente = false;
    this.contadorService.getMeuDescontoVigente().subscribe({
      next: (contrato) => {
        this.contratoVigente = contrato ?? null;
        // Sincroniza o switch com o estado real do contrato vigente.
        this.autonomiaAtiva = contrato?.autonomiaContador ?? false;
        // Mapeia o DTO -> view-model do card compartilhado (detalhe do contrato).
        this.aplicarContratoVigente(this.contratoVigente);
        this.carregandoContratoVigente = false;
        this.contratoVigenteResolvido = true;
        this.avaliarVisibilidadeAbas();
      },
      error: () => {
        this.contratoVigente = null;
        this.autonomiaAtiva = false;
        this.aplicarContratoVigente(null);
        this.erroContratoVigente = true;
        this.carregandoContratoVigente = false;
        this.contratoVigenteResolvido = true;
        this.avaliarVisibilidadeAbas();
      },
    });
  }

  /**
   * Mapeia MeuDescontoVigenteDTO -> ContratoInfoVM para o card compartilhado.
   * Campos ausentes/nulos (contratos legados) caem no placeholder do card ("-").
   */
  private aplicarContratoVigente(contrato: MeuDescontoVigenteDTO | null): void {
    if (!contrato) {
      this.contratoInfoVM = null;
      this.contratoMesReferencia = null;
      this.moedaConfig = MOEDA_CONFIG[Moeda.GUARANI];
      return;
    }
    this.contratoMesReferencia = contrato.mesReferencia ?? null;
    const codigo = (contrato.moeda?.enum as Moeda) ?? Moeda.GUARANI;
    this.moedaConfig = MOEDA_CONFIG[codigo] ?? MOEDA_CONFIG[Moeda.GUARANI];
    this.contratoInfoVM = {
      // No contexto do cliente, o "nome" do card e o CONTADOR (rotulo "Contador").
      nome: contrato.nomeContador,
      status: contrato.status,
      valorMensal: contrato.valorMensal ?? null,
      moedaNome: contrato.moeda?.nome ?? null,
      percentualDesconto: contrato.percentualDesconto,
      diaCobranca: contrato.diaCobranca ?? null,
      parcelasPorMes: (contrato as { parcelasPorMes?: number }).parcelasPorMes ?? null,
      criadoEm: contrato.criadoEm ?? null,
      dataVencimento: contrato.dataVencimento ?? null,
      autonomiaContador: contrato.autonomiaContador,
    };
  }

  /** Opcoes do mes de referencia (rotulos traduzidos; refeitas a cada troca de idioma). */
  private buildMesReferenciaOptions(): void {
    const valores: MesReferencia[] = ['MES_ANTERIOR', 'MES_ATUAL', 'PROXIMO_MES'];
    this.mesReferenciaOptions = valores.map((valor) => ({
      value: valor,
      label: this.mesReferenciaContratoLabel(valor),
    }));
  }

  /** Rotulo localizado do mes de referencia (reusa as chaves do Operar Cliente). */
  private mesReferenciaContratoLabel(mes: MesReferencia): string {
    const chave = `contador_operar.despesa_acessoria.mes_referencia_valores.${mes.toLowerCase()}`;
    const traduzido = this.translate.instant(chave);
    return traduzido === chave ? mes : traduzido;
  }

  // ===========================================================================
  // Autonomia do contador (conceder/revogar)
  // ===========================================================================

  /**
   * Reage ao clique no switch de autonomia. Conceder (LIGAR) e ato de confianca:
   * abre o dialogo de confirmacao antes de aplicar e, se o usuario cancelar/fechar,
   * o switch VOLTA ao estado anterior (ver reverterAutonomiaSwitch). Revogar
   * (DESLIGAR) e direto (sem confirmacao).
   */
  onAutonomiaToggle(event: { checked: boolean }): void {
    if (event.checked) {
      // Estado confirmado (fonte da verdade = contrato). Ao cancelar o dialogo,
      // o switch e restaurado para este valor — nunca um `false` fixo.
      const valorAnterior = this.contratoVigente?.autonomiaContador ?? false;
      this.abrirConfirmacao({
        titulo: 'notificacoes.autonomia.dialog_conceder_titulo',
        subtitulo: this.contratoVigente?.nomeContador,
        mensagem: 'notificacoes.autonomia.confirmar_conceder',
        icone: 'pi pi-shield',
        variante: 'info',
        botaoLabel: 'notificacoes.autonomia.conceder',
        acao: () => this.definirAutonomia(true),
        aoCancelar: () => this.reverterAutonomiaSwitch(valorAnterior),
      });
    } else {
      this.definirAutonomia(false);
    }
  }

  /**
   * Restaura o switch de autonomia ao valor informado (o estado confirmado do
   * contrato). Reatribui o modelo e forca o p-inputSwitch a refleti-lo via
   * `writeValue`, pois reverter para o MESMO valor anterior no mesmo ciclo de
   * change detection nao dispara a sincronizacao automatica do [(ngModel)].
   */
  private reverterAutonomiaSwitch(valorAnterior: boolean): void {
    this.autonomiaAtiva = valorAnterior;
    this.autonomiaSwitch?.writeValue(valorAnterior);
  }

  /**
   * Aplica a autonomia via PATCH e atualiza o estado pelo RETORNO (nao otimista).
   * Em erro, reverte o switch para o estado real do contrato e exibe o toast.
   */
  private definirAutonomia(autonomia: boolean): void {
    const contratoId = this.contratoVigente?.contratoId;
    if (contratoId == null || this.definindoAutonomia) {
      // Sem contrato ATIVO com id nao ha o que atualizar: reverte o switch.
      this.autonomiaAtiva = this.contratoVigente?.autonomiaContador ?? false;
      return;
    }
    this.definindoAutonomia = true;
    this.contadorService.definirAutonomia(contratoId, autonomia).subscribe({
      next: (contrato) => {
        this.definindoAutonomia = false;
        if (this.contratoVigente) {
          this.contratoVigente.autonomiaContador = contrato.autonomiaContador;
        }
        this.autonomiaAtiva = contrato.autonomiaContador;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant(
            contrato.autonomiaContador
              ? 'notificacoes.autonomia.concedida_sucesso'
              : 'notificacoes.autonomia.revogada_sucesso',
          ),
        });
      },
      error: (err) => {
        this.definindoAutonomia = false;
        // Reverte para o estado real do contrato (o switch pode ter ficado ligado
        // ao revogar sem confirmacao, ou o backend pode ter recusado a mudanca).
        this.autonomiaAtiva = this.contratoVigente?.autonomiaContador ?? false;
        this.messageService.add({
          severity: 'error',
          summary: err?.error?.message || this.translate.instant('notificacoes.erro'),
        });
      },
    });
  }

  // ===========================================================================
  // Solicitacoes de contrato
  // ===========================================================================

  carregarContratos(): void {
    this.carregandoContratos = true;
    this.erroContratos = false;
    this.contadorService.listarSolicitacoesRecebidas().subscribe({
      next: (lista) => {
        this.contratos = lista || [];
        this.carregandoContratos = false;
      },
      error: () => {
        this.contratos = [];
        this.erroContratos = true;
        this.carregandoContratos = false;
      },
    });
  }

  aceitarContrato(contrato: SolicitacaoContratoRecebidaDTO): void {
    this.abrirConfirmacao({
      titulo: 'notificacoes.contrato.dialog_aceitar_titulo',
      subtitulo: contrato.nomeContador,
      mensagem: 'notificacoes.contrato.confirmar_aceitar',
      icone: 'pi pi-check',
      variante: 'info',
      botaoLabel: 'notificacoes.contrato.aceitar',
      acao: () => this.executarAcaoContrato(contrato, true),
    });
  }

  recusarContrato(contrato: SolicitacaoContratoRecebidaDTO): void {
    this.abrirConfirmacao({
      titulo: 'notificacoes.contrato.dialog_recusar_titulo',
      subtitulo: contrato.nomeContador,
      mensagem: 'notificacoes.contrato.confirmar_recusar',
      icone: 'pi pi-times',
      variante: 'danger',
      botaoLabel: 'notificacoes.contrato.recusar',
      acao: () => this.executarAcaoContrato(contrato, false),
    });
  }

  private executarAcaoContrato(contrato: SolicitacaoContratoRecebidaDTO, aceitar: boolean): void {
    if (this.processando) {
      return;
    }
    this.processando = true;
    const requisicao = aceitar
      ? this.contadorService.aceitarContrato(contrato.contratoId)
      : this.contadorService.recusarContrato(contrato.contratoId);

    requisicao.subscribe({
      next: () => {
        this.processando = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant(
            aceitar ? 'notificacoes.aceitar_sucesso' : 'notificacoes.recusar_sucesso',
          ),
        });
        // A solicitacao sai da lista de pendentes nos dois casos.
        this.carregarContratos();
        if (aceitar) {
          // O contrato aceito passa a ser o contrato ATIVO do cliente: recarrega
          // a secao "Meu contrato" (que ate aqui exibe "sem contrato ativo") e
          // libera a aba "Parcelas do contrato", que so existe com contrato
          // ATIVO. Sem isso, os dados so apareciam apos F5.
          this.reiniciarParcelasCliente();
          this.carregarContratoVigente();
        }
      },
      error: (err) => {
        this.processando = false;
        this.messageService.add({
          severity: 'error',
          summary: err?.error?.message || this.translate.instant('notificacoes.erro'),
        });
      },
    });
  }

  // ===========================================================================
  // Despesas acessorias pendentes
  // ===========================================================================

  carregarDespesas(): void {
    this.carregarDespesas$().subscribe();
  }

  /**
   * Fluxo de carga das despesas pendentes: atualiza os flags de estado e
   * devolve o Observable da requisicao (com o erro ja tratado). Encapsular a
   * carga em um Observable permite encadea-la com `switchMap` na troca de
   * idioma, cancelando uma recarga anterior ainda em curso.
   */
  private carregarDespesas$(): Observable<DespesaAcessoriaPendenteDTO[]> {
    this.carregandoDespesas = true;
    this.erroDespesas = false;
    return this.despesaService.listarPendentes().pipe(
      tap((lista) => {
        this.despesas = lista || [];
        this.carregandoDespesas = false;
        this.despesasResolvidas = true;
        this.avaliarVisibilidadeAbas();
      }),
      catchError(() => {
        this.despesas = [];
        this.erroDespesas = true;
        this.carregandoDespesas = false;
        this.despesasResolvidas = true;
        this.avaliarVisibilidadeAbas();
        return of([] as DespesaAcessoriaPendenteDTO[]);
      }),
    );
  }

  // ===========================================================================
  // Visibilidade do bloco de abas
  // ===========================================================================

  /**
   * Decide (UMA UNICA VEZ, ver `abasAvaliadas`) se o bloco de abas deve ser
   * exibido. Chamado ao fim das DUAS cargas iniciais que alimentam a decisao:
   * o contrato vigente e as despesas pendentes.
   *
   * Regra: o bloco so some quando NAO HA NADA — sem contrato ativo, zero
   * pendentes e zero historico. Em qualquer duvida (carga ainda em curso, erro
   * em uma das chamadas, sondagem que falhou) o bloco PERMANECE visivel:
   * esconder conteudo que existe e muito pior do que exibir uma tabela vazia.
   *
   * O caso ambiguo (sem contrato E sem pendentes) e o unico que paga um GET
   * extra — a sondagem do historico em `sondarHistoricoVazio`. Quem tem
   * contrato ativo ou pendencia nao paga nada.
   */
  private avaliarVisibilidadeAbas(): void {
    if (this.abasAvaliadas) {
      return;
    }
    // Alguma das duas cargas ainda nao terminou: aguarda a proxima chamada.
    if (!this.contratoVigenteResolvido || !this.despesasResolvidas) {
      return;
    }
    // Erro em qualquer uma delas: nao da para AFIRMAR que esta vazio.
    if (this.erroContratoVigente || this.erroDespesas) {
      this.abasAvaliadas = true;
      this.mostrarAbas = true;
      return;
    }
    // Ha contrato ativo ou despesa pendente: exibe sem custo adicional.
    if (this.contratoVigente || this.despesas.length > 0) {
      this.abasAvaliadas = true;
      this.mostrarAbas = true;
      return;
    }
    // Caso ambiguo: pode ser "nunca teve contador" (esconde) ou "ja teve, o
    // contrato acabou, mas o historico existe" (mantem). So o backend sabe.
    this.abasAvaliadas = true;
    this.sondarHistoricoVazio();
  }

  /**
   * SONDAGEM do historico — o unico GET extra da tela, disparado apenas no caso
   * ambiguo. Pede a primeira pagina com `size=1` somente para ler o
   * `totalElements`.
   *
   * NAO PODE tocar em NENHUM estado da aba 2 (`historico`, `historicoFirst`,
   * `historicoRows`, `historicoTotalRecords`, `historicoRowsSolicitado`,
   * `historicoSort` ou `historicoIniciado`): a aba continua LAZY e a sua
   * primeira carga real segue vindo do (onLazyLoad) do p-table. Contaminar
   * esses campos quebraria a paginacao server-side e a recarga pos-aprovacao.
   *
   * Erro na sondagem => mantem o bloco visivel (falha segura).
   */
  private sondarHistoricoVazio(): void {
    this.despesaService.listarHistorico(0, 1).subscribe({
      next: (res) => {
        this.mostrarAbas = (res?.totalElements ?? 0) > 0;
      },
      error: () => {
        this.mostrarAbas = true;
      },
    });
  }

  // ===========================================================================
  // Historico de despesas decididas (aprovadas/recusadas)
  // ===========================================================================

  /**
   * Busca uma pagina do historico no backend (paginacao/ordenacao server-side).
   * `sort` no formato Spring `campo,direcao`; omitido => default do backend.
   */
  carregarHistorico(
    page: number = 0,
    size: number = this.historicoRows,
    sort: string | undefined = this.historicoSort,
  ): void {
    this.historicoRowsSolicitado = size;
    this.carregandoHistorico = true;
    this.erroHistorico = false;
    this.despesaService.listarHistorico(page, size, sort).subscribe({
      next: (res) => {
        this.historico = res.content || [];
        this.historicoTotalRecords = res.totalElements ?? 0;
        this.historicoRows = size;
        this.historicoFirst = page * size;
        this.carregandoHistorico = false;
      },
      error: () => {
        this.historico = [];
        this.historicoTotalRecords = 0;
        this.erroHistorico = true;
        this.carregandoHistorico = false;
      },
    });
  }

  /**
   * Lazy-load do p-table: dispara na primeira renderizacao e ao clicar num
   * cabecalho ordenavel. Mapeia o campo da coluna -> whitelist do backend
   * (`valor`/`dataPagamento`/`tipoDespesa`/`status`/`dataDecisao`/`pagoEm`; o
   * `sortField` do p-table ja e o campo do backend) e a direcao (1=asc/-1=desc).
   * A coluna "Contador" (nomeContador) nao e ordenavel — sem `pSortableColumn`.
   */
  onHistoricoLazyLoad(event: TableLazyLoadEvent): void {
    this.historicoIniciado = true;
    const size = event.rows ?? this.historicoRows;
    const page = Math.floor((event.first ?? 0) / size);
    const sortField = typeof event.sortField === 'string' ? event.sortField : null;
    const sortOrder = event.sortOrder ?? 1;
    this.historicoSort = sortField ? `${sortField},${sortOrder === 1 ? 'asc' : 'desc'}` : undefined;
    // Difere a carga para fora do ciclo de change detection corrente: o p-table
    // emite o PRIMEIRO onLazyLoad durante a renderizacao do conteudo da aba
    // (pTemplate="content"), e mutar estado bindado ([loading]) sincronamente
    // nesse momento dispara NG0100 (ExpressionChangedAfterItHasBeenChecked).
    setTimeout(() => this.carregarHistorico(page, size, this.historicoSort));
  }

  /**
   * Reage a troca de aba do p-tabView. Dispara a 1a carga das parcelas do
   * contrato do cliente quando a aba "Parcelas do contrato" e aberta pela
   * primeira vez.
   *
   * Resolucao do indice: as duas primeiras abas ("Despesas do contador" e
   * "Historico de gastos") sao incondicionais e a aba de parcelas so existe
   * (via *ngIf) quando ha contrato ATIVO com id, sempre como ULTIMA aba —
   * logo, quando renderizada, ocupa o indice fixo PARCELAS_TAB_INDEX (2). O
   * guard `contratoVigente?.contratoId != null` confirma que a aba existe.
   *
   * A carga e guardada por `parcelasClienteIniciado` (uma unica vez) e deferida
   * com setTimeout: mutar [carregando] durante a change detection da ativacao
   * da aba (pTemplate="content" monta o grid nesse ciclo) dispara NG0100.
   */
  onTabViewChange(event: { index: number }): void {
    if (
      event.index === NotificacoesContadorComponent.PARCELAS_TAB_INDEX &&
      !this.parcelasClienteIniciado &&
      this.contratoVigente?.contratoId != null
    ) {
      this.parcelasClienteIniciado = true;
      setTimeout(() => this.carregarParcelasCliente(0));
    }
  }

  /** Troca de pagina pelo paginador externo (recarrega a pagina do backend). */
  onHistoricoPageChange(event: { page?: number; rows?: number }): void {
    const size = event.rows ?? this.historicoRows;
    const page = event.page ?? 0;
    this.carregarHistorico(page, size, this.historicoSort);
  }

  /** Troca de "itens por pagina": volta para a primeira pagina e recarrega. */
  onHistoricoRowsPerPageChange(): void {
    if (this.historicoRows === this.historicoRowsSolicitado) {
      return; // emissao inicial do dropdown, sem mudanca real (ver campo acima)
    }
    this.carregarHistorico(0, this.historicoRows, this.historicoSort);
  }

  /** Recarrega a pagina ATUAL do historico (mesmo sort) — apos aprovar/recusar. */
  private recarregarHistorico(): void {
    const page = this.historicoRows > 0 ? Math.floor(this.historicoFirst / this.historicoRows) : 0;
    this.carregarHistorico(page, this.historicoRows, this.historicoSort);
  }

  /**
   * So faz sentido navegar para contas a pagar quando o gasto foi APROVADO
   * (ATIVA) — nesse caso o backend gera o titulo a pagar do cliente (SAIDA),
   * inclusive para gastos especiais. RECUSADA nao gera titulo.
   */
  podeVerContaPagar(item: DespesaAcessoriaHistoricoDTO): boolean {
    return item.status === 'ATIVA';
  }

  /**
   * Navega para a listagem de Contas a Pagar (SAIDA), onde vive o titulo gerado
   * pela aprovacao do gasto. Deep-link direto aos titulos da PESSOA do contador
   * (`/contas-a-pagar/pessoa/:id`) depende de o backend expor a `pessoaId` do
   * contador no cadastro do cliente no historico — ver relatorio. Por ora, leva
   * a listagem geral (mesma rota do menu "Contas a pagar").
   */
  verContaPagar(item: DespesaAcessoriaHistoricoDTO): void {
    if (!this.podeVerContaPagar(item)) {
      return;
    }
    this.router.navigate(['/contas-pagar-receber'], { queryParams: { tipo: 'SAIDA' } });
  }

  aprovarDespesa(despesa: DespesaAcessoriaPendenteDTO): void {
    this.abrirConfirmacao({
      titulo: 'notificacoes.despesa.dialog_aprovar_titulo',
      subtitulo: despesa.nomeContador,
      mensagem: 'notificacoes.despesa.confirmar_aprovar',
      icone: 'pi pi-check',
      variante: 'info',
      botaoLabel: 'notificacoes.despesa.aprovar',
      acao: () => this.executarAcaoDespesa(despesa, true),
    });
  }

  recusarDespesa(despesa: DespesaAcessoriaPendenteDTO): void {
    this.abrirConfirmacao({
      titulo: 'notificacoes.despesa.dialog_recusar_titulo',
      subtitulo: despesa.nomeContador,
      mensagem: 'notificacoes.despesa.confirmar_recusar',
      icone: 'pi pi-times',
      variante: 'danger',
      botaoLabel: 'notificacoes.despesa.recusar',
      acao: () => this.executarAcaoDespesa(despesa, false),
    });
  }

  private executarAcaoDespesa(despesa: DespesaAcessoriaPendenteDTO, aprovar: boolean): void {
    if (this.processando || despesa.id == null) {
      return;
    }
    this.processando = true;
    const requisicao = aprovar
      ? this.despesaService.aprovar(despesa.id)
      : this.despesaService.recusar(despesa.id);

    requisicao.subscribe({
      next: () => {
        this.processando = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant(
            aprovar
              ? 'notificacoes.despesa.aprovar_sucesso'
              : 'notificacoes.despesa.recusar_sucesso',
          ),
        });
        // O item sai das pendentes e entra no historico — recarrega as duas.
        // O historico so e recarregado se a aba 2 ja foi aberta (senao a
        // primeira ativacao ja busca os dados frescos), mantendo a pagina
        // atual e a ordenacao ativa.
        this.carregarDespesas();
        if (this.historicoIniciado) {
          this.recarregarHistorico();
        }
      },
      error: (err) => {
        this.processando = false;
        this.messageService.add({
          severity: 'error',
          summary: err?.error?.message || this.translate.instant('notificacoes.erro'),
        });
      },
    });
  }

  // ===========================================================================
  // Helpers de exibicao
  // ===========================================================================

  /**
   * Rotulo localizado do tipo de despesa. O backend serializa TipoDespesa como
   * objeto LocalizableEnum ({ enum, nome, ... }) com `nome` ja traduzido;
   * aceita string por legado.
   */
  tipoDespesaNome(tipo: TipoDespesaBackendDTO | string | null | undefined): string {
    if (tipo == null) {
      return '-';
    }
    if (typeof tipo === 'object') {
      return tipo.nome || tipo.enum || '-';
    }
    return tipo;
  }

  /**
   * Indica se o tipo de gasto e relacionado a IVA (destaque na tabela).
   * Delega ao util compartilhado para manter a convencao em um unico lugar.
   */
  ehIva(tipo: TipoDespesaBackendDTO | string | null | undefined): boolean {
    return ehIvaTipoDespesa(tipo);
  }

  /**
   * Formata o valor mensal do contrato conforme a moeda (objeto localizado do
   * backend). GUARANI sem casas decimais; demais com duas. Delega ao util
   * compartilhado.
   */
  formatarValorMoeda(valor: number | null, moedaEnum: string | null | undefined): string {
    return formatarValorMoedaContrato(valor, moedaEnum);
  }

  /** Cor (severity do p-tag) do status do historico: ATIVA=verde, RECUSADA=vermelho. */
  historicoStatusSeverity(status: string): 'success' | 'danger' {
    return status === 'ATIVA' ? 'success' : 'danger';
  }

  /** Rotulo localizado do status do historico (Aprovado / Recusado). */
  historicoStatusLabel(status: string): string {
    const chave =
      status === 'ATIVA'
        ? 'notificacoes.despesa.status_aprovado'
        : 'notificacoes.despesa.status_recusado';
    return this.translate.instant(chave);
  }

  /**
   * Formata a data da decisao (LocalDateTime ISO) para dd/MM/yyyy. Retorna '-'
   * quando nula (legado sem data).
   */
  formatarDataDecisao(dataDecisao: string | null): string {
    if (!dataDecisao) {
      return '-';
    }
    const data = new Date(dataDecisao);
    if (isNaN(data.getTime())) {
      return '-';
    }
    const lang = this.translate.currentLang || 'pt-BR';
    const locale = lang === 'es' ? 'es-PY' : 'pt-BR';
    return data.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  // ===========================================================================
  // Situacao de pagamento (tag Pago / A pagar)
  // ===========================================================================

  /**
   * A tag de pagamento só faz sentido para gastos ATIVA (aprovados) e NÃO
   * especiais. A especial não gera título a receber rastreado por `pagoEm`,
   * então não deve virar "A pagar". Nos demais status a coluna Situação já cobre.
   */
  mostrarTagPagamento(item: DespesaAcessoriaHistoricoDTO): boolean {
    return item.status === 'ATIVA';
  }

  /** True quando o CLIENTE já quitou o próprio título (lado SAIDA). */
  private pago(item: DespesaAcessoriaHistoricoDTO): boolean {
    return item.pagoPeloClienteEm != null;
  }

  /** Rótulo localizado da tag de pagamento (Pago / A pagar). */
  pagamentoLabel(item: DespesaAcessoriaHistoricoDTO): string {
    return this.translate.instant(
      this.pago(item) ? 'notificacoes.despesa.pago' : 'notificacoes.despesa.a_pagar',
    );
  }

  /** Cor (severity do p-tag) da tag de pagamento. */
  pagamentoSeverity(item: DespesaAcessoriaHistoricoDTO): 'success' | 'warning' {
    return this.pago(item) ? 'success' : 'warning';
  }

  /** Tooltip da tag de pagamento: quando pago, mostra a data do pagamento. */
  pagamentoTooltip(item: DespesaAcessoriaHistoricoDTO): string {
    if (!this.pago(item) || !item.pagoPeloClienteEm) {
      return '';
    }
    return this.translate.instant('notificacoes.despesa.pago_em', {
      data: this.formatarDataDecisao(item.pagoPeloClienteEm),
    });
  }

  // ===========================================================================
  // Parcelas do contrato do cliente (secao read-only)
  // ===========================================================================

  /**
   * Carrega a PAGINA informada das parcelas do contrato do cliente (paginacao e
   * ordenacao server-side, somente leitura). `parcelasClienteFirst` reflete o
   * offset da pagina carregada. Sem contrato ativo com id, nao faz nada (a secao
   * fica oculta no template).
   */
  carregarParcelasCliente(page: number = 0): void {
    const contratoId = this.contratoVigente?.contratoId;
    if (contratoId == null) {
      return;
    }
    this.carregandoParcelasCliente = true;
    this.erroParcelasCliente = false;
    this.contadorService
      .listarParcelasDoMeuContrato(
        contratoId,
        page,
        this.parcelasClienteRows,
        this.montarSortParcelasCliente(),
        this.montarFiltrosParcelasCliente(),
      )
      .subscribe({
        next: (res) => {
          this.parcelasCliente = res.content || [];
          this.parcelasClienteTotalRecords = res.totalElements ?? 0;
          this.parcelasClienteFirst = page * this.parcelasClienteRows;
          this.carregandoParcelasCliente = false;
        },
        error: () => {
          this.parcelasCliente = [];
          this.parcelasClienteTotalRecords = 0;
          this.erroParcelasCliente = true;
          this.carregandoParcelasCliente = false;
        },
      });
  }

  /**
   * Ordenacao por cabecalho das parcelas (server-side). Ignora quando o clique
   * repete o mesmo campo+direcao ja aplicados. Volta para a primeira pagina.
   */
  onParcelasClienteSort(event: { field?: string | string[]; order?: number }): void {
    const campo = Array.isArray(event.field) ? event.field[0] : event.field ?? null;
    const ordem = event.order ?? 1;
    if (campo === this.parcelasClienteSortField && ordem === this.parcelasClienteSortOrder) {
      return;
    }
    this.parcelasClienteSortField = campo;
    this.parcelasClienteSortOrder = ordem;
    this.parcelasClienteFirst = 0;
    this.carregarParcelasCliente(0);
  }

  /** Troca de pagina pelo paginador do grid (recarrega a pagina do backend). */
  onParcelasClientePageChange(event: { page?: number; rows?: number }): void {
    const size = event.rows ?? this.parcelasClienteRows;
    const page = event.page ?? 0;
    this.parcelasClienteRows = size;
    this.carregarParcelasCliente(page);
  }

  /** Troca de "itens por pagina": volta para a primeira pagina e recarrega. */
  onParcelasClienteRowsPerPageChange(): void {
    this.parcelasClienteFirst = 0;
    this.carregarParcelasCliente(0);
  }

  /**
   * Aplica o filtro de periodo (app-periodo-filtro) sobre as parcelas. Converte
   * as datas para ISO (yyyy-MM-dd) e recarrega a partir da primeira pagina.
   */
  onParcelasClientePeriodoChanged(periodo: PeriodoFiltro): void {
    this.parcelasClienteDe = this.dataFiltroParaIso(periodo?.dataInicio);
    this.parcelasClienteAte = this.dataFiltroParaIso(periodo?.dataFim);
    this.parcelasClienteFirst = 0;
    this.carregarParcelasCliente(0);
  }

  /**
   * Aplica o filtro de situacao (estado do cliente) emitido pelo grid. O cast e
   * seguro: as opcoes passadas ao grid nesta tela sao de EstadoClienteParcela.
   */
  onParcelasClienteSituacaoChange(valor: string | null): void {
    this.parcelasClienteSituacao = (valor as EstadoClienteParcela | null) ?? null;
    this.parcelasClienteFirst = 0;
    this.carregarParcelasCliente(0);
  }

  /** Retry (botao "tentar novamente" do grid): recarrega a pagina atual. */
  recarregarParcelasCliente(): void {
    this.carregarParcelasCliente(this.parcelasClientePaginaAtual());
  }

  /**
   * Zera o estado das parcelas do cliente ao trocar o contrato vigente (aceite
   * de uma nova solicitacao). Libera a carga lazy (`parcelasClienteIniciado`)
   * para que a aba "Parcelas do contrato" busque os dados do contrato NOVO na
   * proxima abertura, em vez de exibir a pagina do contrato anterior.
   */
  private reiniciarParcelasCliente(): void {
    this.parcelasClienteIniciado = false;
    this.parcelasCliente = [];
    this.parcelasClienteTotalRecords = 0;
    this.parcelasClienteFirst = 0;
    this.erroParcelasCliente = false;
  }

  /** Monta os filtros (periodo + situacao) para o GET de parcelas do cliente. */
  private montarFiltrosParcelasCliente(): ParcelaContratoClienteFiltroDTO {
    return {
      de: this.parcelasClienteDe,
      ate: this.parcelasClienteAte,
      situacao: this.parcelasClienteSituacao ?? undefined,
    };
  }

  /**
   * Monta o `sort` das parcelas no formato Spring (`campo,asc|desc`). Sem
   * ordenacao de cabecalho retorna o default do backend (`numeroParcela,asc`).
   */
  private montarSortParcelasCliente(): string {
    if (!this.parcelasClienteSortField || this.parcelasClienteSortOrder === 0) {
      return 'numeroParcela,asc';
    }
    return `${this.parcelasClienteSortField},${this.parcelasClienteSortOrder === 1 ? 'asc' : 'desc'}`;
  }

  /**
   * Monta as opcoes do dropdown de situacao (estado do cliente) com rotulos
   * traduzidos. A primeira opcao ("Todas") = null. Reconstruida ao trocar idioma.
   */
  private buildParcelasClienteSituacaoOptions(): void {
    this.parcelasClienteSituacaoOptions = [
      {
        value: null,
        label: this.translate.instant('contador_operar.despesa_acessoria.parcelas.filtro_situacao_todas'),
      },
      ...NotificacoesContadorComponent.SITUACOES_PARCELA_CLIENTE.map((s) => ({
        value: s,
        label: this.estadoClienteLabel(s),
      })),
    ];
  }

  /** Rotulo localizado da situacao do CLIENTE (usado so para montar as opcoes). */
  private estadoClienteLabel(estado: EstadoClienteParcela): string {
    const chave = `contador_operar.despesa_acessoria.parcelas.estado_cliente.${estado.toLowerCase()}`;
    const traduzido = this.translate.instant(chave);
    return traduzido === chave ? estado : traduzido;
  }

  /**
   * Converte a data emitida pelo app-periodo-filtro (`dd/MM/yyyyTHH:mm`) para o
   * formato `yyyy-MM-dd` esperado pelo backend. Vazio/indefinido => undefined.
   * (Duplicado do Operar Cliente — candidato a util compartilhado no futuro.)
   */
  private dataFiltroParaIso(valor: string | undefined | null): string | undefined {
    if (!valor) {
      return undefined;
    }
    const parteData = valor.split('T')[0];
    const partes = parteData.split('/');
    if (partes.length !== 3) {
      return undefined;
    }
    const [dia, mes, ano] = partes;
    return `${ano}-${mes}-${dia}`;
  }

  /** Indice 0-based da pagina atual das parcelas (offset / itens por pagina). */
  private parcelasClientePaginaAtual(): number {
    return this.parcelasClienteRows > 0
      ? Math.floor(this.parcelasClienteFirst / this.parcelasClienteRows)
      : 0;
  }
}
